import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import TextInput from "../../../components/ui/TextInput";
import Button from "../../../components/ui/Button";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import Icon from "../../../components/common/Icon";
import {
    createAudienceProfile,
    updateAudienceProfile,
    generateAudienceDescription,
    AudienceProfile,
} from "../../../api/audienceProfile";
import { useNotification } from "../../../context/NotificationContext";
import styles from "./EditAudienceProfileModal.module.css";

interface EditAudienceProfileModalProps {
    isOpen: boolean;
    onClose: (created?: boolean) => void;
    audienceProfile: AudienceProfile | null;
    browserLanguage: string;
    onProfileCreated?: (profile: AudienceProfile) => void;
}

const EditAudienceProfileModal: React.FC<EditAudienceProfileModalProps> = ({
    isOpen,
    onClose,
    audienceProfile,
    browserLanguage,
    onProfileCreated,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();
    const modalContentRef = useRef<HTMLDivElement>(null);

    const isEditMode = audienceProfile !== null;

    const [name, setName] = useState(audienceProfile?.name || "");
    const [description, setDescription] = useState(audienceProfile?.description || "");
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        if (audienceProfile) {
            setName(audienceProfile.name);
            setDescription(audienceProfile.description || "");
        } else {
            setName("");
            setDescription("");
        }
    }, [audienceProfile, isOpen]);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, handleOutsideClick]);

    const handleGenerateDescription = async () => {
        if (!name.trim()) {
            showNotification({
                message: t("audienceProfiles.missingName") || "Please enter a name first",
                type: "error",
            });
            return;
        }

        setIsGenerating(true);

        try {
            const result = await generateAudienceDescription(name, description, browserLanguage);
            setDescription(result.description);

            if (result.quota.remaining <= 10) {
                showNotification({
                    message:
                        t("audienceProfiles.quotaLow", { remaining: result.quota.remaining }) ||
                        `${result.quota.remaining} generations remaining today`,
                    type: "info",
                });
            }
        } catch (error: any) {
            console.error("Error generating description:", error);

            if (error.message?.includes("429") || error.message?.includes("quota")) {
                showNotification({
                    message: t("audienceProfiles.quotaExceeded") || "Daily limit reached. Try again tomorrow.",
                    type: "error",
                });
            } else {
                showNotification({
                    message: t("audienceProfiles.generateError") || "Failed to generate description",
                    type: "error",
                });
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSave = async () => {
        // Validation
        if (!name.trim()) {
            showNotification({
                message: t("audienceProfiles.missingName") || "Please enter a name",
                type: "error",
            });
            return;
        }

        if (description.length > 1000) {
            showNotification({
                message: t("audienceProfiles.descriptionTooLong") || "Description must be 1000 characters or less",
                type: "error",
            });
            return;
        }

        setIsSaving(true);

        try {
            if (isEditMode && audienceProfile) {
                await updateAudienceProfile({
                    audienceProfileId: audienceProfile.id,
                    name: name.trim(),
                    description: description.trim(),
                });

                showNotification({
                    message: t("audienceProfiles.updatedSuccessfully") || "Audience profile updated successfully",
                    type: "success",
                });
            } else {
                const newProfile = await createAudienceProfile({
                    name: name.trim(),
                    description: description.trim(),
                });

                showNotification({
                    message: t("audienceProfiles.createdSuccessfully") || "Audience profile created successfully",
                    type: "success",
                });

                if (onProfileCreated) {
                    onProfileCreated(newProfile);
                }
            }

            onClose(true);
        } catch (error: any) {
            console.error("Error saving audience profile:", error);
            let errorMessage = isEditMode
                ? t("audienceProfiles.updateError") || "Failed to update audience profile"
                : t("audienceProfiles.createError") || "Failed to create audience profile";

            if (error.message) {
                if (error.message.includes("max-audience-profiles-reached")) {
                    errorMessage = t("audienceProfiles.maxReached") || "Maximum of 10 audience profiles reached";
                } else if (error.message.includes("400")) {
                    errorMessage = t("audienceProfiles.invalidData") || "Invalid data provided";
                } else if (error.message.includes("404")) {
                    errorMessage = t("audienceProfiles.notFound") || "Audience profile not found";
                }
            }

            showNotification({
                message: errorMessage,
                type: "error",
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} ref={modalContentRef}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        {isEditMode
                            ? t("audienceProfiles.editTitle") || "Edit Audience Profile"
                            : t("audienceProfiles.createTitle") || "Create Audience Profile"}
                    </h2>
                    <button className={styles.closeButton} onClick={() => onClose()}>
                        &times;
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.section}>
                        <label className={styles.label}>{t("audienceProfiles.nameLabel") || "Name"}</label>
                        <TextInput
                            placeholder={
                                t("audienceProfiles.namePlaceholder") ||
                                "e.g., History enthusiasts, Young professionals..."
                            }
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>

                    <div className={styles.section}>
                        <label className={styles.label}>
                            {t("audienceProfiles.descriptionLabel") || "Description"}
                        </label>
                        <textarea
                            className={styles.descriptionTextarea}
                            placeholder={
                                t("audienceProfiles.descriptionPlaceholder") || "Describe your target audience..."
                            }
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={8}
                        />
                        <div className={styles.textareaFooter}>
                            <div className={styles.characterCount}>
                                {description.length}/1000 {t("audienceProfiles.characters") || "characters"}
                            </div>
                            <button
                                className={styles.generateButton}
                                onClick={handleGenerateDescription}
                                disabled={isGenerating || !name.trim()}
                            >
                                {isGenerating ? (
                                    <>
                                        <LoadingSpinner size={8} />
                                        {t("audienceProfiles.generating") || "Generating..."}
                                    </>
                                ) : (
                                    <>
                                        <Icon name="ic-sparkles" />
                                        {t("audienceProfiles.generateDescription") || "Générer la description"}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    {isSaving ? (
                        <LoadingSpinner size={20} />
                    ) : (
                        <>
                            <Button variant="cancel" label={t("cancel")} onClick={() => onClose()} />
                            <Button variant="primary" label={t("save")} onClick={handleSave} />
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default EditAudienceProfileModal;
